// src/LoginModal.js
import React, { useState, useEffect } from "react";
import {
  saveTokens,
  saveUser,
  saveRememberedUsername,
  loadRememberedUsername,
  clearRememberedUsername,
} from "./auth";

const BACKEND = "https://staging.parkengage.ai/api";

export default function LoginModal({ onClose, onLoginSuccess }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [userType, setUserType] = useState("partner"); // partner | parker
  const [loading, setLoading] = useState(false);

  // toast state
  const [toast, setToast] = useState(null); // { type: "success" | "error", msg: string }

  useEffect(() => {
    const saved = loadRememberedUsername();
    if (saved) {
      setUsername(saved);
      setRemember(true);
    }
  }, []);

  const showToast = (type, msg, timeout = 2500) => {
    setToast({ type, msg });
    if (timeout) {
      setTimeout(() => setToast(null), timeout);
    }
  };

  async function sendSecretToBackend(partnerId, partnerSecret, authHeader) {
    if (!partnerId || !partnerSecret) return;
    try {
      await fetch(`${BACKEND}/user/secret`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": authHeader,
        },
        body: JSON.stringify({ partner_id: partnerId, partner_secret: partnerSecret }),
      });
      // intentionally ignoring response body here (backend validates & stores)
    } catch (err) {
      console.warn("Failed to send partner secret to backend:", err);
    }
  }

  async function sendUserToBackend(userObj, partnerId, authHeader) {
    if (!partnerId || !userObj) return;
    try {
      await fetch(`${BACKEND}/user/upload`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": authHeader,
        },
        body: JSON.stringify({ partner_id: String(partnerId), user: userObj }),
      });
    } catch (err) {
      console.warn("Failed to upload user object to backend:", err);
    }
  }

  async function submit(e) {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    setToast(null);

    try {
      const res = await fetch(`${BACKEND}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ username, password, user_type: userType }),
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || "Invalid username or password");
      }

      // response contains tokens + merged user payload per updated backend
      const body = await res.json();

      // Extract and persist only the token bits
      const tokens = {
        access_token: body.access_token,
        refresh_token: body.refresh_token,
        token_type: body.token_type || "Bearer",
        expires_in: body.expires_in,
        obtained_at: Date.now(),
      };

      saveTokens(tokens);

      // Save user info (the backend returns `user` object which now contains oauth-derived fields)
      const userData = body.user || {};
      if (body.user_type) userData.user_type = body.user_type;
      saveUser(userData, remember);

      // Now: ALWAYS attempt to extract partner_id & partner_secret from returned payload (works for partner or parker)
      const partnerId =
        body.partner_id ||
        userData.partner_id ||
        userData.id ||
        (userData.data && (userData.data.partner_id || userData.data.id)) ||
        (userData.user && (userData.user.partner_id || userData.user.id)) ||
        null;

      const partnerSecret =
        body.partner_secret ||
        userData.partner_secret ||
        (userData.oauth && userData.oauth.token_section && userData.oauth.token_section.client_secret) ||
        userData.client_secret ||
        (userData.data && (userData.data.partner_secret || (Array.isArray(userData.data.secret) ? userData.data.secret[0] : null))) ||
        (Array.isArray(userData.secret) ? userData.secret[0] : userData.secret) ||
        null;

      const authHeader = `${tokens.token_type} ${tokens.access_token}`;

      if (partnerId && partnerSecret) {
        // persist to localStorage (existing frontend code expects these keys)
        // NOTE: for production, remove storing raw partner_secret in localStorage.
        try {
          localStorage.setItem("partner_id", String(partnerId));
          localStorage.setItem("secret", partnerSecret);
        } catch (err) {
          console.warn("Failed to write partner credentials to localStorage", err);
        }

        // forward to backend for safe/encrypted storage
        await sendSecretToBackend(partnerId, partnerSecret, authHeader);
      }

      // also upload full user JSON to backend (one-time canonical store)
      await sendUserToBackend(userData, partnerId, authHeader).catch(() => {});

      // remember username only
      if (remember) saveRememberedUsername(username);
      else clearRememberedUsername();

      showToast("success", "✅ Login successful");

      // let user see success briefly, then close
      setTimeout(() => {
        onLoginSuccess?.(userData);
        onClose?.();
      }, 700);
    } catch (err) {
      console.error(err);
      showToast("error", err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="relative bg-white rounded-xl p-6 w-full max-w-md shadow-xl animate-fadeIn">

        {/* Toast */}
        {toast && (
          <div
            className={`absolute -top-12 left-1/2 -translate-x-1/2 px-4 py-2 rounded-md text-sm shadow
              ${toast.type === "success"
                ? "bg-green-600 text-white"
                : "bg-red-600 text-white"
              }`}
          >
            {toast.msg}
          </div>
        )}

        <h3 className="text-xl font-semibold text-gray-800 mb-1">
          Welcome back
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Sign in to continue
        </p>

        <form onSubmit={submit} className="space-y-4">
          <input
            required
            disabled={loading}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Email"
            autoComplete="username"
            className="w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
          />

          <input
            required
            disabled={loading}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            autoComplete="current-password"
            className="w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
          />

          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={remember}
              disabled={loading}
              onChange={(e) => setRemember(e.target.checked)}
            />
            Remember my username
          </label>

          {/* User type selector */}
          <div className="text-sm text-gray-700">
            <label className="block mb-1">Login as</label>
            <select
              value={userType}
              onChange={(e) => setUserType(e.target.value)}
              disabled={loading}
              className="w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            >
              <option value="partner">Partner</option>
              <option value="parker">Parker</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 rounded-md border text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-60"
            >
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
